// =============================================================================
// features/configuracoes/comercial/contactWindowMath.ts — cálculo puro do
// preview da janela de contato NO FUSO CONFIGURADO (nunca no fuso do
// navegador — doc 26 §6.9, nota explícita do slot: "já foi um bug de
// produção grave em outra feature (computeRange)").
//
// Mesma técnica de apps/api/src/modules/sales/settings/window.ts
// (zonedWallTime): `Intl.DateTimeFormat` com `timeZone` explícito decompõe
// um instante em dia-da-semana/hora NAQUELE fuso — nunca no fuso do processo
// que roda o código (aqui, o navegador do usuário). Duplicado no frontend
// porque window.ts não está em `files_allowed` deste slot (apps/api/**).
// =============================================================================

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const WEEKDAY_MAP: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export interface ZonedWallTime {
  /** Dia da semana no fuso — convenção `getUTCDay()` (0=domingo..6=sábado). */
  weekday: number;
  /** "HH:MM" no fuso pedido. */
  time: string;
}

/** `true` quando `timeZone` é um identificador IANA válido no runtime atual. */
export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone.trim()) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Decompõe `date` (instante real, ex: `new Date()`) nos componentes de
 * dia-da-semana/hora do fuso pedido. Retorna `null` para fuso inválido —
 * o chamador decide o fallback (nunca silenciosamente usa o fuso do
 * navegador).
 */
export function zonedWallTime(date: Date, timeZone: string): ZonedWallTime | null {
  if (!isValidTimeZone(timeZone)) return null;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
  });

  const byType: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') byType[part.type] = part.value;
  }

  const weekday = WEEKDAY_MAP[byType.weekday ?? ''] ?? 0;
  const hour = (byType.hour ?? '00').padStart(2, '0');
  const minute = (byType.minute ?? '00').padStart(2, '0');

  return { weekday, time: `${hour}:${minute}` };
}

export function weekdayLabel(day: number): string {
  return WEEKDAY_LABELS[day] ?? '?';
}

export function weekdaysLabel(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return sorted.map(weekdayLabel).join(', ');
}

/** Espelha `isWithinContactWindow` do backend (mesma convenção de comparação lexicográfica HH:MM). */
export function isWithinWindowAt(
  wall: ZonedWallTime,
  window: { days: number[]; startTime: string; endTime: string },
): boolean {
  if (!window.days.includes(wall.weekday)) return false;
  return wall.time >= window.startTime && wall.time < window.endTime;
}
